/**
 * useUsers — context for dynamically loaded users from PocketBase.
 *
 * The UsersProvider fetches all users from the `tracker_users` PB collection
 * on mount and provides them via context. This replaces the hardcoded
 * USERS, USER_LABELS, USER_EMOJIS constants from useUserView.
 *
 * Each app wraps its root in <UsersProvider pb={pocketbaseInstance}>.
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import PocketBase from 'pocketbase';

const UsersContext = createContext({
    users: [],
    loading: true,
});

/**
 * Fetches users from PocketBase `tracker_users` collection and provides
 * them to the component tree.
 *
 * @param {Object} props
 * @param {string} [props.pocketbaseUrl] - PocketBase URL (defaults to VITE_POCKETBASE_URL env var)
 * @param {React.ReactNode} props.children
 */
export function UsersProvider({ pocketbaseUrl, children }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const pbRef = useRef(null);

    const url = pocketbaseUrl || import.meta.env.VITE_POCKETBASE_URL;

    useEffect(() => {
        if (!url) {
            console.error('UsersProvider: No PocketBase URL provided');
            setLoading(false);
            return;
        }

        if (!pbRef.current) {
            pbRef.current = new PocketBase(url);
        }
        const pb = pbRef.current;
        let cancelled = false;

        pb.collection('tracker_users')
            .getFullList({ sort: 'name', $autoCancel: false })
            .then((records) => {
                if (!cancelled) {
                    setUsers(records.map((r) => ({
                        id: r.id,
                        name: r.name,
                        displayName: r.displayName,
                        emoji: r.emoji,
                        steamId: r.steamId || null,
                        xboxGamertag: r.xboxGamertag || null,
                        clerkId: r.clerkId || null,
                    })));
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.error('Failed to fetch users:', err);
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [url]);

    const value = useMemo(() => ({ users, loading }), [users, loading]);

    return (
        <UsersContext.Provider value={value}>
            {children}
        </UsersContext.Provider>
    );
}

/**
 * Hook to access the loaded users.
 *
 * @returns {{ users: User[], loading: boolean }}
 *
 * User shape:
 *   { id, name, displayName, emoji, steamId, xboxGamertag }
 */
export function useUsers() {
    return useContext(UsersContext);
}

/**
 * Derives USER_LABELS map from users array.
 * Includes 'pod' and 'all' entries for view labels.
 */
export function deriveUserLabels(users) {
    const labels = {
        pod: 'Together',
        all: 'All',
    };
    for (const u of users) {
        labels[u.name] = u.displayName;
    }
    return labels;
}

/**
 * Derives USER_EMOJIS map from users array.
 */
export function deriveUserEmojis(users) {
    const emojis = {};
    for (const u of users) {
        emojis[u.name] = u.emoji;
    }
    return emojis;
}

/**
 * Derives VIEW_OPTIONS from users array.
 *
 * @param {Array} users - All tracker_users
 * @param {Object} [opts] - Options
 * @param {string} [opts.currentUserName] - Current user's name (for isolation mode)
 * @param {Array} [opts.podMemberNames] - Names of pod members (null = no pod)
 * @returns {string[]} View options
 */
export function deriveViewOptions(users, opts = {}) {
    const { currentUserName, podMemberNames } = opts;

    if (currentUserName) {
        if (podMemberNames && podMemberNames.length > 0) {
            // In a pod: show self + pod members + combined views
            const others = podMemberNames.filter(n => n !== currentUserName);
            return [currentUserName, ...others, 'pod', 'all'];
        }
        // No pod: isolation mode — only self
        return [currentUserName];
    }

    // No current user context yet — default to first user only (no combined views)
    if (users.length > 0) return [users[0].name];
    return [];
}

export default useUsers;
